import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Rtl2SsoInterface } from '@rtl2/sso';
import { Rtl2UiMenu } from '@rtl2/ui';
import { SharedHttpError } from '@shared/models';
import { SharedEnvironmentService } from '@shared/services';

const LOGGER_SERVICE_SEPARATOR = ': ';

@Injectable({
  providedIn: 'root',
})
export class SharedSsoInterfaceService implements Rtl2SsoInterface {
  menu: Observable<Rtl2UiMenu>;
  private excludeType: string[];
  private authRoles: string[];

  constructor(private environment: SharedEnvironmentService) {
    this.excludeType = this.environment.get('loggerExcludeType') || [];
    const roles = (this.authRoles = this.environment.get('authRoles'));
    this.menu = new Observable(function (observer) {
      observer.next({
        environments: {
          test: {
            app: 't-propo.rtl2cloud.de',
          },
          integration: {
            app: 'i-propo.rtl2cloud.de',
          },
          production: {
            app: 'p-propo.rtl2cloud.de',
          },
        },
        permissions: {
          disabled: { test: {}, integration: {}, production: {} },
          roles: {
            app: [...roles],
          },
        },
        metadata: {
          app: {
            id: 'app',
            name: 'routeTitle.producersPortal',
            tags: [],
            description: '',
          },
        },
      });
    });
  }

  getMenuRoles(name: string): Observable<string[]> {
    return of([]);
  }

  getMenuRolesCurrent(): Observable<string[]> {
    return of([...this.authRoles]);
  }

  logInfo(msg: any, context?: string) {
    this.write('INFO', msg, context);
  }

  logError(msg: any, context?: string) {
    this.write('ERROR', msg, context);
  }

  logNotice(msg: any, context?: string) {
    this.write('NOTICE', msg, context);
  }

  logWarning(msg: any, context?: string) {
    this.write('WARNING', msg, context);
  }

  logDebug(msg: any, context?: string) {
    this.write('DEBUG', msg, context);
  }

  environmentGet(name: string): any {
    return this.environment.get(name) || null;
  }

  handleError(err: HttpErrorResponse | any): SharedHttpError {
    if (err.error instanceof Error) {
      this.logError(err.error.message.toString(), 'CLIENT-SIDE or NETWORK ERROR');
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong
      if (typeof err === 'string') {
        try {
          this.logError(JSON.parse(err), 'BACKEND RETURNED ERROR');
        } catch (e) {
          this.logError(err, 'BACKEND RETURNED ERROR');
        }
        return new SharedHttpError({ body: err });
      }
    }
    return new SharedHttpError({
      status: err.error.status,
      url: err.error.url,
      body: err.error.message.toString(),
    });
  }

  private write(type: string, msg: any, context?: string) {
    if (this.excludeType.indexOf(type) > -1) {
      return;
    }

    let logMsg: string;
    logMsg = type;

    if (context) {
      logMsg += LOGGER_SERVICE_SEPARATOR + context;
    }

    if (msg instanceof Array) {
      const newMsg = [];
      msg.forEach((v) => {
        newMsg.push(v);
        newMsg.push('\n');
      });
      msg = newMsg.slice(0, -1);
    } else {
      msg = [msg];
    }
    this.writeConsole(type, logMsg, msg);
  }

  private writeConsole(type: string, logMessage: string, msg: any[]) {
    const log = '%c' + logMessage;
    const style = 'color: $c; background-color: $bc; padding: 2px 5px';
    msg = ['\n', ...msg];
    switch (type) {
      case 'INFO':
        console.info(log, style.replace('$c', 'white').replace('$bc', 'DodgerBlue'), ...msg);
        break;
      case 'NOTICE':
        console.log(
          log,
          style.replace('$c', 'DarkOliveGreen').replace('$bc', 'GreenYellow'),
          ...msg
        );
        break;
      case 'DEBUG':
        console.log(log, style.replace('$c', 'white').replace('$bc', 'DodgerBlue'), ...msg);
        break;
      case 'ERROR':
        console.error(log, style.replace('$c', 'white').replace('$bc', 'red'), ...msg);
        break;
      case 'WARNING':
        console.warn(log, style.replace('$c', 'white').replace('$bc', 'DarkOrange'), ...msg);
        break;
    }
  }
}
